import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import roddoFish from "../../assets/roddo-fish.png";
import FishIQSpeciesImageBase from "../FishIQSpeciesImage";
import { getFishIQEnvironmentCompatibility } from "../../services/fishIQLocationRanker";
import { buildFishIQSpeciesTacticalFusion } from "../../lib/fishiq/fishIQMovementRead";

const FishIQSpeciesImage =
  FishIQSpeciesImageBase as React.ComponentType<any>;

const fishQuickUniqueList = (input: any, limit = 4): string[] => {
  const src = Array.isArray(input)
    ? input
    : typeof input === "string"
      ? input.split(/|,|•|·/g)
      : [];

  const seen = new Set<string>();
  const out: string[] = [];

  for (const item of src) {
    const value = String(item ?? "").trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
    if (out.length >= limit) break;
  }

  return out;
};

const firstFishText = (...values: any[]): string => {
  for (const value of values) {
    if (!value) continue;

    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }

    if (Array.isArray(value)) {
      const hit = value.map((v) => String(v ?? "").trim()).find(Boolean);
      if (hit) return hit;
    }
  }

  return "";
};

const shortenFishLine = (value: any, max = 140): string => {
  const text = String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}…`;
};

const formatCatchPopupDate = (timestamp: any): string => {
  const raw = Number(timestamp ?? 0);
  if (!Number.isFinite(raw) || raw <= 0) return "Saved catch";

  const ms = raw < 1_000_000_000_000 ? raw * 1000 : raw;
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "Saved catch";

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const fishQuickChips = (
  species: any,
  spot: any,
): Array<{ label: string; tone: "water" | "bait" | "tech" | "neutral" }> => {
  const habitats = fishQuickUniqueList(
    species?.habitats ||
      species?.waterTypes ||
      species?.topWaterTypes ||
      species?.waterContext?.habitats ||
      [],
    2,
  );

  const baits = fishQuickUniqueList(
    species?.tactics?.bestBaits || species?.bestBaits || species?.baits || [],
    1,
  );

  const techniques = fishQuickUniqueList(
    species?.tactics?.bestTechniques ||
      species?.bestTechniques ||
      species?.techniques ||
      [],
    1,
  );

  const extras = fishQuickUniqueList(
    [
      spot?.waterName,
      species?.waterName,
      species?.waterType,
      species?.waterTypeKey,
      ...(Array.isArray(species?.topWaterTypes) ? species.topWaterTypes : []),
    ],
    1,
  );

  const rankLabel = String(
    species?.__fishiq?.locationRank?.shortLabel ||
      species?.__fishiq?.confidenceLabel ||
      "",
  ).trim();

  const chips = [
    rankLabel ? { label: rankLabel, tone: "neutral" as const } : null,
    habitats[0] ? { label: habitats[0], tone: "water" as const } : null,
    extras[0] ? { label: extras[0], tone: "water" as const } : null,
    baits[0] ? { label: baits[0], tone: "bait" as const } : null,
    techniques[0] ? { label: techniques[0], tone: "tech" as const } : null,
  ].filter(Boolean) as Array<{
    label: string;
    tone: "water" | "bait" | "tech" | "neutral";
  }>;

  const seen = new Set<string>();
  return chips.filter((chip) => {
    const key = String(chip.label || "")
      .trim()
      .toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const quickChipStyle = (
  isDarkMode: boolean,
  tone: "water" | "bait" | "tech" | "neutral",
) => {
  if (tone === "water") {
    return {
      background: isDarkMode
        ? "rgba(124,199,255,0.14)"
        : "rgba(234,247,255,0.96)",
      color: isDarkMode ? "#DDF4FF" : "#1C6B94",
      borderColor: isDarkMode
        ? "rgba(124,199,255,0.20)"
        : "rgba(191,230,255,1)",
    };
  }

  if (tone === "bait") {
    return {
      background: isDarkMode
        ? "rgba(255,217,119,0.14)"
        : "rgba(255,243,207,0.96)",
      color: isDarkMode ? "#FFE9B4" : "#7B5900",
      borderColor: isDarkMode
        ? "rgba(255,217,119,0.18)"
        : "rgba(241,215,143,1)",
    };
  }

  if (tone === "tech") {
    return {
      background: isDarkMode
        ? "rgba(185,145,255,0.14)"
        : "rgba(242,236,255,0.96)",
      color: isDarkMode ? "#F0E6FF" : "#7751C7",
      borderColor: isDarkMode
        ? "rgba(185,145,255,0.18)"
        : "rgba(216,198,255,1)",
    };
  }

  return {
    background: isDarkMode
      ? "rgba(255,255,255,0.08)"
      : "rgba(255,255,255,0.82)",
    color: isDarkMode ? "#EDF2FF" : "#6A5E52",
    borderColor: isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(230,219,206,1)",
  };
};

export const getFishAreaEmoji = (
  label?: string | null,
  waterLabel?: string | null,
): string => {
  const haystack = `${label || ""} ${waterLabel || ""}`.toLowerCase();
  if (/(crab|crawfish|crayfish|shrimp|lobster)/i.test(haystack)) return "🦀";
  if (/(clam|oyster|mussel|scallop)/i.test(haystack)) return "🦪";
  if (/(eel|snakehead)/i.test(haystack)) return "🐍";
  if (/(trout|salmon|steelhead)/i.test(haystack)) return "🐟";
  if (/(bass|bluegill|perch|zander|pike|drum|catfish)/i.test(haystack))
    return "🐠";

  const fishPool = ["🐟", "🐠", "🐡", "🎣"];
  const seed = `${label || ""}${waterLabel || ""}`;
  const score = Array.from(seed).reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return fishPool[score % fishPool.length];
};

const decorateAreaBadgeLabel = (label?: string | null): string => {
  const clean = String(label || "").trim();
  if (!clean) return "";
  if (/catch|memory/i.test(clean)) return `🎣 ${clean}`;
  if (/rule|list|open/i.test(clean)) return `🐟 ${clean}`;
  if (/season|window|night|morning|dawn|dusk/i.test(clean))
    return `🐠 ${clean}`;
  if (/habitat|profile|regional|area/i.test(clean)) return `🐡 ${clean}`;
  return `🐟 ${clean}`;
};

const fishIQMapPremiumMicroStyles = `
@keyframes roddoFishIQMapPremiumIn {
  from { opacity: 0; transform: translateY(6px) scale(0.995); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.roddo-fishiq-map-premium-in {
  animation: roddoFishIQMapPremiumIn 180ms cubic-bezier(0.22, 1, 0.36, 1) both;
}
@media (prefers-reduced-motion: reduce) {
  .roddo-fishiq-map-premium-in { animation: none !important; }
}
`;

type FishSpeciesRowProps = {
  isDarkMode: boolean;
  speciesLabel: string;
  speciesData?: any;
  waterLabel: string;
  primaryBadgeLabel: string;
  catchCountLabel?: string | null;
  summaryLabel: string;
  canOpenDetail: boolean;
  showChevron?: boolean;
  icon?: string | null;
  onClick: () => void;
};

export const FishSpeciesRow: React.FC<FishSpeciesRowProps> = ({
  isDarkMode,
  speciesLabel,
  speciesData,
  catchCountLabel,
  canOpenDetail,
  showChevron = canOpenDetail,
  icon,
  onClick,
}) => {
  const scientificName = String(
    speciesData?.scientificName || speciesData?.taxonomy?.scientificName || "",
  ).trim();
  const isCaught = String(catchCountLabel || "").startsWith("✓");

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full min-h-[62px] overflow-hidden rounded-[16px] border px-2.5 py-2 text-left transition-[transform,box-shadow,background-color] duration-150 active:scale-[0.985] sm:min-h-[66px] sm:px-3 sm:py-2.5"
      style={{
        background: isCaught
          ? isDarkMode
            ? "rgba(18,48,39,0.96)"
            : "rgba(239,251,245,0.98)"
          : isDarkMode
            ? "rgba(15,24,40,0.92)"
            : "rgba(245,243,239,0.95)",
        borderColor: isCaught
          ? isDarkMode
            ? "rgba(102,214,169,0.28)"
            : "rgba(117,201,164,0.42)"
          : isDarkMode
            ? "rgba(132,151,190,0.10)"
            : "rgba(91,67,49,0.08)",
        boxShadow: isCaught
          ? isDarkMode
            ? "0 5px 14px rgba(0,0,0,0.16), inset 0 1px 0 rgba(157,245,210,0.04)"
            : "0 5px 14px rgba(56,122,93,0.08), inset 0 1px 0 rgba(255,255,255,0.82)"
          : isDarkMode
            ? "0 3px 10px rgba(0,0,0,0.10)"
            : "0 4px 11px rgba(89,65,37,0.035), inset 0 1px 0 rgba(255,255,255,0.64)",
        color: isCaught
          ? isDarkMode
            ? "#EAFBF4"
            : "#294D40"
          : isDarkMode
            ? "#C4CCDA"
            : "#6E7274",
      }}
    >
      <div className="flex items-center gap-3">
        <FishIQSpeciesImage
          species={speciesData || { commonName: speciesLabel, name: speciesLabel }}
          size="thumb"
          fallback={
            icon && !/[🐟🐠🐡]/u.test(icon) ? (
              <span aria-hidden="true">{icon}</span>
            ) : (
              <img
                src={roddoFish}
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain"
                draggable={false}
              />
            )
          }
          className="h-11 w-[58px] shrink-0 rounded-[13px] border text-[20px] sm:h-12 sm:w-[64px] sm:rounded-[14px]"
          imageClassName={
            isCaught
              ? "object-cover saturate-[1.06] contrast-[1.04] opacity-100"
              : "object-cover saturate-[0.72] contrast-[1.03] opacity-[0.82]"
          }
          isDarkMode={isDarkMode}
        />

        <div className="min-w-0 flex-1">
          <div
            className="truncate text-[15px] font-black leading-tight sm:text-[16px]"
            style={{
              color: isCaught
                ? isDarkMode
                  ? "#F1FFF8"
                  : "#294D40"
                : isDarkMode
                  ? "#C4CCDA"
                  : "#5F666A",
            }}
          >
            {speciesLabel}
          </div>
          {scientificName ? (
            <div
              className="mt-1 truncate text-[10px] italic leading-4 sm:text-[11px]"
              style={{
                color: isCaught
                  ? isDarkMode
                    ? "rgba(188,226,210,0.82)"
                    : "rgba(62,103,86,0.72)"
                  : isDarkMode
                    ? "rgba(160,170,186,0.64)"
                    : "rgba(103,103,100,0.58)",
              }}
            >
              {scientificName}
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {catchCountLabel ? (
            <span
              className="rounded-full border px-2 py-1 text-[9px] font-black tracking-[0.02em]"
              style={{
                background: catchCountLabel.startsWith("✓")
                  ? isDarkMode
                    ? "rgba(102,214,169,0.12)"
                    : "rgba(234,255,245,0.96)"
                  : isDarkMode
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(248,245,239,0.96)",
                borderColor: catchCountLabel.startsWith("✓")
                  ? isDarkMode
                    ? "rgba(102,214,169,0.20)"
                    : "rgba(172,235,204,1)"
                  : isDarkMode
                    ? "rgba(255,255,255,0.10)"
                    : "rgba(91,67,49,0.10)",
                color: catchCountLabel.startsWith("✓")
                  ? isDarkMode
                    ? "#DFFFF1"
                    : "#256A54"
                  : isDarkMode
                    ? "#AEB8C9"
                    : "#756A60",
              }}
            >
              {catchCountLabel}
            </span>
          ) : null}

        {showChevron ? (
          <div
            aria-hidden="true"
            className="grid h-8 w-8 place-items-center rounded-full border text-[17px] font-black sm:h-9 sm:w-9"
            style={{
              background: isCaught
                ? isDarkMode
                  ? "rgba(102,214,169,0.08)"
                  : "rgba(255,255,255,0.66)"
                : isDarkMode
                  ? "rgba(9,15,26,0.72)"
                  : "rgba(255,255,255,0.48)",
              borderColor: isCaught
                ? isDarkMode
                  ? "rgba(102,214,169,0.18)"
                  : "rgba(117,201,164,0.28)"
                : isDarkMode
                  ? "rgba(132,151,190,0.10)"
                  : "rgba(91,67,49,0.08)",
              color: isCaught
                ? isDarkMode
                  ? "#BFEFD9"
                  : "#4B7765"
                : isDarkMode
                  ? "#8792A4"
                  : "rgba(95,90,82,0.48)",
            }}
          >
            ›
          </div>
        ) : null}
        </div>
      </div>
    </button>
  );
};

export const getAreaActivityBadgeLabel = (index: number) => {
  if (index <= 0) return "Regional";
  if (index === 1) return "Habitat fit";
  if (index === 2) return "Seasonal";
  return "Profile";
};

export const getFishRulesBadgeLabel = (
  speciesLabel: string,
  speciesData?: any,
): string | null => {
  const kind = String(speciesData?.aquaticKind || "").toLowerCase();
  const name = String(
    speciesLabel || speciesData?.commonName || speciesData?.name || "",
  ).toLowerCase();
  const caution = String(speciesData?.caution || "").toLowerCase();
  const harvestModes = Array.isArray(speciesData?.harvestModes)
    ? speciesData.harvestModes
    : [];

  const isShellfishLike =
    ["crustacean", "mollusk", "cephalopod", "echinoderm"].includes(kind) ||
    /(crab|shrimp|prawn|lobster|crawfish|crayfish|clam|oyster|mussel|scallop|squid|octopus|abalone|urchin)/i.test(
      name,
    );

  if (isShellfishLike)
    return caution.includes("closure") ? "Check closures" : "Check rules";
  if (
    harvestModes.length ||
    /(limit|season|slot|closed|closure|regulated|license)/i.test(caution)
  )
    return "Check rules";
  return null;
};

export const getHotspotLogCatchCoords = (
  spot: any,
  fallback?: { lat?: number; lon?: number } | null,
): { lat: number; lon: number } | null => {
  const lat = Number(spot?.position?.lat ?? spot?.coords?.lat ?? fallback?.lat);
  const lon = Number(
    spot?.position?.lng ??
      spot?.position?.lon ??
      spot?.coords?.lng ??
      spot?.coords?.lon ??
      fallback?.lon,
  );

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return { lat, lon };
};

export const MapFishQuickCard: React.FC<{
  species: any;
  spot: any;
  isDarkMode: boolean;
  isActive?: boolean;
  onBack: () => void;
  onOpenFull: () => void;
  onOpenFishIQ?: () => void;
  onLogCatch?: () => void;
  onOpenCatchHistory?: (speciesName: string) => void;
}> = ({ species, spot, isDarkMode, isActive = true, onBack, onOpenFull, onLogCatch, onOpenCatchHistory }) => {
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const [selectedMemoryCatchId, setSelectedMemoryCatchId] = useState<string | null>(null);
  const memoryCatches = Array.isArray((spot as any)?.__roddoCatches)
    ? ((spot as any).__roddoCatches as MyCatchMapEntry[])
    : [];
  const selectedMemoryCatch =
    memoryCatches.find((entry) => entry.id === selectedMemoryCatchId) || null;

  useEffect(() => {
    if (!isImageExpanded) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsImageExpanded(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isImageExpanded]);

  const commonName =
    String(
      species?.commonName ||
        species?.name ||
        spot?.species ||
        spot?.name ||
        "Unknown species",
    ).trim() || "Unknown species";

  const scientificName = String(
    species?.scientificName || species?.taxonomy?.scientificName || "",
  ).trim();

  const summaryLine = shortenFishLine(
    firstFishText(
      species?.summary,
      species?.descriptionShort,
      species?.description,
      species?.notes,
      species?.overview,
    ),
    150,
  );

  const habitats = fishQuickUniqueList(
    species?.habitats ||
      species?.waterTypes ||
      species?.topWaterTypes ||
      species?.waterContext?.habitats ||
      [],
    3,
  );

  const bestBaits = fishQuickUniqueList(
    species?.tactics?.bestBaits || species?.bestBaits || species?.baits || [],
    3,
  );

  const bestTechniques = fishQuickUniqueList(
    species?.tactics?.bestTechniques ||
      species?.bestTechniques ||
      species?.techniques ||
      [],
    3,
  );

  const bestTimes = fishQuickUniqueList(
    species?.tactics?.bestTimesOfDay || species?.bestTimesOfDay || [],
    2,
  );

  const areaRead = (spot as any)?.fishIQAreaRead || null;
  const normalizeConfidenceIdentity = (value: any) =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  const predictionRows = Array.isArray(areaRead?.species) ? areaRead.species : [];
  const commonNameKey = normalizeConfidenceIdentity(commonName);
  const scientificNameKey = normalizeConfidenceIdentity(scientificName);
  const speciesIdKey = normalizeConfidenceIdentity(species?.id || species?.slug || species?.canonicalSpeciesId);
  const speciesPrediction =
    predictionRows.find((prediction: any) => {
      const predictionNameKey = normalizeConfidenceIdentity(prediction?.name);
      const predictionScientificKey = normalizeConfidenceIdentity(prediction?.scientificName);
      const predictionIdKey = normalizeConfidenceIdentity(prediction?.speciesId || prediction?.canonicalSpeciesId);
      return Boolean(
        (scientificNameKey && predictionScientificKey === scientificNameKey) ||
        (speciesIdKey && predictionIdKey === speciesIdKey) ||
        (commonNameKey && predictionNameKey === commonNameKey),
      );
    }) || null;
  const speciesConfidenceScoreRaw = Number(speciesPrediction?.confidence);
  const speciesConfidenceScore = Number.isFinite(speciesConfidenceScoreRaw)
    ? Math.max(0, Math.min(100, Math.round(speciesConfidenceScoreRaw)))
    : null;
  const speciesConfidenceLabel = String(speciesPrediction?.confidenceLabel || "").trim();
  const speciesConfidenceBreakdown = speciesPrediction?.confidenceBreakdown || null;
  const confidenceComponents = Array.isArray(speciesConfidenceBreakdown?.components)
    ? speciesConfidenceBreakdown.components.filter((component: any) => component?.available !== false)
    : [];
  const compactConfidenceComponents = [
    ["regional", "Regional"],
    ["habitat", "Habitat"],
    ["behavior", "Behavior"],
    ["environment", "Live water"],
  ]
    .map(([key, label]) => {
      const component = confidenceComponents.find((row: any) => row?.key === key);
      const score = Number(component?.score);
      return component && Number.isFinite(score)
        ? { key, label, score: Math.max(0, Math.min(100, Math.round(score))) }
        : null;
    })
    .filter(Boolean) as Array<{ key: string; label: string; score: number }>;
  const confidenceCap = String(speciesConfidenceBreakdown?.cappedBy || "").trim();
  const environmentalContext = areaRead?.environmentalContext || null;
  const fusion = areaRead?.fusion || null;
  const rawTacticalFusion = areaRead?.tacticalFusion || null;
  const environmentCompatibility = getFishIQEnvironmentCompatibility(
    species,
    {
      lat: Number(areaRead?.lat ?? spot?.position?.lat),
      lon: Number(areaRead?.lng ?? spot?.position?.lng ?? spot?.position?.lon),
      waterName: areaRead?.waterName ?? spot?.waterName ?? null,
      waterType: areaRead?.waterType ?? spot?.waterType ?? null,
      waterKind: areaRead?.waterKind ?? spot?.waterKind ?? null,
      regionHint: areaRead?.regionHint ?? null,
      source: 'map-fish-quick-card',
    },
    environmentalContext,
  );
  const liveCurrent =
    environmentCompatibility.currentCompatible &&
    environmentalContext?.current?.available === true
      ? environmentalContext.current
      : null;
  const liveBathymetry =
    environmentCompatibility.bathymetryCompatible &&
    environmentalContext?.bathymetry?.available === true
      ? environmentalContext.bathymetry
      : null;
  const hasIncompatibleWaterIntelComponent = Boolean(
    (environmentalContext?.current?.available === true && !environmentCompatibility.currentCompatible) ||
      (environmentalContext?.bathymetry?.available === true && !environmentCompatibility.bathymetryCompatible),
  );
  const tacticalFusion = hasIncompatibleWaterIntelComponent
    ? null
    : buildFishIQSpeciesTacticalFusion(rawTacticalFusion, species);
  const liveWaterActive = Boolean(
    environmentalContext &&
      environmentCompatibility.compatible &&
      (liveCurrent || liveBathymetry || fusion?.weatherAvailable),
  );
  const fusionConfidence = Math.max(
    0,
    Math.min(100, Math.round(Number(fusion?.confidence || environmentalContext?.environmentConfidence || 0) * 100)),
  );
  const currentSpeed = Number(liveCurrent?.speedKnots);
  const currentDirection = String(liveCurrent?.directionLabel || '').trim();
  const currentStrength = String(liveCurrent?.strength || '').trim();
  const currentFact = liveCurrent
    ? [
        Number.isFinite(currentSpeed)
          ? `${currentSpeed < 1 ? currentSpeed.toFixed(2) : currentSpeed.toFixed(1)} kt`
          : '',
        currentDirection,
      ].filter(Boolean).join(' ')
    : '';
  const depthMeters = Number(liveBathymetry?.depthMeters);
  const reliefMeters = Number(liveBathymetry?.localReliefMeters);
  const liveStructures = Array.isArray(liveBathymetry?.structureTypes)
    ? liveBathymetry.structureTypes
        .map((value: any) => String(value || '').trim())
        .filter((value: string) => value && value.toLowerCase() !== 'unknown')
    : [];
  const primaryStructure = String(liveStructures[0] || '').replace(/[_-]+/g, ' ').trim();
  const depthFact = Number.isFinite(depthMeters)
    ? `${Math.round(depthMeters)} m depth`
    : '';
  const reliefFact = Number.isFinite(reliefMeters) && reliefMeters > 0
    ? `${Math.round(reliefMeters)} m relief`
    : '';
  const liveWaterFacts = fishQuickUniqueList(
    tacticalFusion?.active && Array.isArray(tacticalFusion?.facts)
      ? tacticalFusion.facts
      : [currentFact, depthFact, primaryStructure, reliefFact].filter(Boolean),
    4,
  );
  const currentTimingLine = liveCurrent
    ? [
        currentFact,
        currentStrength && currentStrength !== 'unknown'
          ? `${currentStrength.charAt(0).toUpperCase()}${currentStrength.slice(1)} flow`
          : '',
      ].filter(Boolean).join(' • ')
    : '';
  const structureWhereLine = liveBathymetry
    ? [primaryStructure || String(liveBathymetry?.slopeClass || '').trim(), depthFact]
        .filter(Boolean)
        .join(' • ')
    : '';

  const whereLine = shortenFishLine(
    firstFishText(
      tacticalFusion?.active ? tacticalFusion?.where : '',
      liveWaterActive ? structureWhereLine : '',
      species?.__fishiq?.locationRank?.waterLabel,
      spot?.waterName,
      species?.waterName,
      habitats[0],
    ) || "Best matching local water",
    62,
  );

  const baseThrowLine = firstFishText(
    species?.__fishiq?.bestBait,
    species?.__fishiq?.locationRank?.bestBait,
    bestBaits[0],
    bestTechniques[0],
  ) || "Match the local forage";
  const throwLine = shortenFishLine(
    liveCurrent && currentStrength && !['unknown', 'slack'].includes(currentStrength.toLowerCase())
      ? `${baseThrowLine} • work with flow`
      : baseThrowLine,
    58,
  );

  const whenLine = shortenFishLine(
    firstFishText(
      tacticalFusion?.active ? tacticalFusion?.when : '',
      liveWaterActive ? currentTimingLine : '',
      species?.__fishiq?.locationRank?.bestWindow,
      species?.__fishiq?.bestWindow,
      bestTimes[0],
      spot?.timeOfDay,
    ) || "Fish the strongest window",
    58,
  );

  const speciesTechnique = firstFishText(
    species?.__fishiq?.bestMove,
    species?.__fishiq?.locationRank?.bestMove,
    species?.bestBet,
    species?.tactic,
    bestTechniques[0],
  );
  const bestMove = shortenFishLine(
    tacticalFusion?.active
      ? firstFishText(tacticalFusion?.bestBet, tacticalFusion?.approach, speciesTechnique)
      : speciesTechnique || "Work the highest-percentage structure first.",
    140,
  );

  return (
    <>
      <style>{fishIQMapPremiumMicroStyles}</style>
    <div
      className="roddo-fishiq-map-premium-in relative flex h-full min-h-0 flex-col overflow-hidden rounded-[22px] border"
      style={{
        background: isDarkMode ? "#0A1120" : "rgba(255,252,248,0.985)",
        borderColor: isDarkMode
          ? "rgba(132,151,190,0.14)"
          : "rgba(91,67,49,0.10)",
        boxShadow: isDarkMode
          ? "0 8px 22px rgba(0,0,0,0.24)"
          : "0 14px 28px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.76)",
      }}
    >
      <div className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-3.5 pb-3.5 pt-3.5 sm:px-4 sm:pb-4 sm:pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div
                className="text-[9px] font-black uppercase tracking-[0.18em]"
                style={{ color: isDarkMode ? "#66D9E8" : "#24779B" }}
              >
                Water read
              </div>
              {speciesConfidenceScore != null ? (
                <span
                  className="rounded-full border px-2 py-0.5 text-[8px] font-black uppercase tracking-[0.12em]"
                  style={{
                    background:
                      speciesConfidenceScore >= 82
                        ? isDarkMode ? "rgba(102,214,169,0.12)" : "rgba(234,255,245,0.96)"
                        : speciesConfidenceScore >= 66
                          ? isDarkMode ? "rgba(102,217,232,0.10)" : "rgba(230,248,252,0.96)"
                          : isDarkMode ? "rgba(242,184,75,0.10)" : "rgba(255,248,221,0.90)",
                    borderColor:
                      speciesConfidenceScore >= 82
                        ? isDarkMode ? "rgba(102,214,169,0.22)" : "rgba(117,201,164,0.36)"
                        : speciesConfidenceScore >= 66
                          ? isDarkMode ? "rgba(102,217,232,0.22)" : "rgba(80,176,196,0.26)"
                          : isDarkMode ? "rgba(242,184,75,0.20)" : "rgba(160,120,40,0.20)",
                    color:
                      speciesConfidenceScore >= 82
                        ? isDarkMode ? "#9AF0CC" : "#28795D"
                        : speciesConfidenceScore >= 66
                          ? isDarkMode ? "#8DE8F4" : "#226F82"
                          : isDarkMode ? "#F2D28A" : "#8A6200",
                  }}
                  title="Evidence-weighted species confidence"
                >
                  {speciesConfidenceLabel || "Local fit"} · {speciesConfidenceScore}%
                </span>
              ) : null}
              {liveWaterActive ? (
                <span
                  className="rounded-full border px-2 py-0.5 text-[8px] font-black uppercase tracking-[0.12em]"
                  style={{
                    background: isDarkMode
                      ? "rgba(102,217,232,0.10)"
                      : "rgba(230,248,252,0.96)",
                    borderColor: isDarkMode
                      ? "rgba(102,217,232,0.22)"
                      : "rgba(80,176,196,0.26)",
                    color: isDarkMode ? "#8DE8F4" : "#226F82",
                  }}
                  title="Roddo is using live Water Intel for this read"
                >
                  Live water{fusionConfidence > 0 ? ` · ${fusionConfidence}%` : ""}
                </span>
              ) : null}
            </div>
            <h3
              className="mt-1 truncate text-[20px] font-black leading-tight"
              style={{ color: isDarkMode ? "#F2F6FC" : "#2D3957" }}
            >
              {commonName}
            </h3>
            {scientificName ? (
              <div
                className="mt-1 truncate text-[11px] italic"
                style={{ color: isDarkMode ? "#AEB8C9" : "#756A60" }}
              >
                {scientificName}
              </div>
            ) : null}
          </div>

          <button
            type="button"
            onClick={onBack}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full border text-[17px] font-black transition active:scale-95"
            style={{
              background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.82)",
              borderColor: isDarkMode
                ? "rgba(132,151,190,0.14)"
                : "rgba(91,67,49,0.10)",
              color: isDarkMode ? "#F2F6FC" : "#5D544B",
            }}
            aria-label="Back to species list"
            title="Back"
          >
            {"‹"}
          </button>
        </div>

        <button
          type="button"
          onClick={() => setIsImageExpanded(true)}
          className="group relative mt-3 block w-full overflow-hidden rounded-[18px] border text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#66D9E8]/60"
          style={{
            borderColor: isDarkMode
              ? "rgba(132,151,190,0.14)"
              : "rgba(91,67,49,0.10)",
            background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.76)",
          }}
          aria-label={`View larger ${commonName} reference image`}
          title="View larger image"
        >
          <FishIQSpeciesImage
            species={species}
            size="hero"
            fallback={
              <div className="grid h-full w-full place-items-center text-[48px]">🐟</div>
            }
            className="aspect-[16/8] w-full sm:aspect-[16/7]"
            imageClassName="object-cover transition-transform duration-200 group-hover:scale-[1.015]"
            isDarkMode={isDarkMode}
          />
          <span
            className="pointer-events-none absolute bottom-2 right-2 grid h-8 w-8 place-items-center rounded-full border text-[13px] backdrop-blur-md"
            style={{
              background: isDarkMode ? "rgba(9,13,24,0.76)" : "rgba(255,255,255,0.88)",
              borderColor: isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(70,60,50,0.12)",
              color: isDarkMode ? "#F2F6FC" : "#3D342D",
            }}
            aria-hidden="true"
          >
            ↗
          </span>
        </button>

        {summaryLine ? (
          <p
            className="mt-3 text-[12.5px] font-semibold leading-5"
            style={{ color: isDarkMode ? "#AEB8C9" : "#665A4F" }}
          >
            {summaryLine}
          </p>
        ) : null}

        {compactConfidenceComponents.length ? (
          <div className="mt-2.5 px-0.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <span
                className="text-[8px] font-black uppercase tracking-[0.15em]"
                style={{ color: isDarkMode ? "#A99AF8" : "#7158D6" }}
              >
                Why this fits
              </span>
              <span
                className="text-[9.5px] font-black leading-4"
                style={{ color: isDarkMode ? "#D9D4F8" : "#5A4C74" }}
              >
                {compactConfidenceComponents
                  .map((component) => `${component.label} ${component.score}%`)
                  .join(" • ")}
              </span>
            </div>
            {confidenceCap ? (
              <div
                className="mt-1 text-[9px] font-semibold leading-4"
                style={{ color: isDarkMode ? "rgba(221,229,240,0.66)" : "rgba(102,90,79,0.72)" }}
              >
                Confidence limited by: {confidenceCap}.
              </div>
            ) : null}
          </div>
        ) : null}

        {liveWaterActive && liveWaterFacts.length ? (
          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 px-0.5">
            <span
              className="text-[8px] font-black uppercase tracking-[0.15em]"
              style={{ color: isDarkMode ? "#66D9E8" : "#24779B" }}
            >
              Water
            </span>
            <span
              className="text-[9.5px] font-black leading-4"
              style={{ color: isDarkMode ? "#DCEEF3" : "#405D65" }}
            >
              {liveWaterFacts.join(" • ")}
            </span>
          </div>
        ) : null}

        {tacticalFusion?.active && tacticalFusion?.rightNow ? (
          <div
            className="mt-3 rounded-[14px] border px-3 py-2.5"
            style={{
              background: isDarkMode ? "rgba(138,112,242,0.08)" : "rgba(246,242,255,0.92)",
              borderColor: isDarkMode ? "rgba(169,154,248,0.16)" : "rgba(138,112,242,0.18)",
            }}
          >
            <div
              className="text-[8px] font-black uppercase tracking-[0.15em]"
              style={{ color: isDarkMode ? "#A99AF8" : "#7158D6" }}
            >
              Right now
            </div>
            <div
              className="mt-1 text-[10.5px] font-bold leading-4"
              style={{ color: isDarkMode ? "#EDE9FF" : "#514463" }}
            >
              {tacticalFusion.rightNow}
            </div>
          </div>
        ) : null}

        <div className="mt-3 grid grid-cols-3 gap-2">
          {[
            { label: "Where", value: whereLine, color: "#66D9E8" },
            { label: "When", value: whenLine, color: "#F2B84B" },
            { label: "Throw", value: throwLine, color: "#66D6A9" },
          ].map((item) => (
            <div
              key={item.label}
              className="min-w-0 rounded-[14px] border px-2.5 py-2.5"
              style={{
                background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.72)",
                borderColor: isDarkMode
                  ? "rgba(132,151,190,0.12)"
                  : "rgba(91,67,49,0.09)",
              }}
            >
              <div
                className="text-[8px] font-black uppercase tracking-[0.15em]"
                style={{ color: isDarkMode ? item.color : "#78695B" }}
              >
                {item.label}
              </div>
              <div
                className="mt-1 line-clamp-3 text-[10.5px] font-bold leading-4"
                style={{ color: isDarkMode ? "#F2F6FC" : "#4F463D" }}
              >
                {item.value}
              </div>
            </div>
          ))}
        </div>

        <div
          className="mt-3 rounded-[15px] border px-3 py-2.5"
          style={{
            background: isDarkMode
              ? "rgba(102,217,232,0.07)"
              : "rgba(234,247,255,0.78)",
            borderColor: isDarkMode
              ? "rgba(102,217,232,0.13)"
              : "rgba(86,174,220,0.20)",
          }}
        >
          <div
            className="text-[8.5px] font-black uppercase tracking-[0.15em]"
            style={{ color: isDarkMode ? "#66D9E8" : "#24779B" }}
          >
            Best bet
          </div>
          <div
            className="mt-1 text-[11.5px] font-bold leading-5"
            style={{ color: isDarkMode ? "#F2F6FC" : "#405463" }}
          >
            {bestMove}
          </div>
        </div>

        {memoryCatches.length ? (
          <div
            className="mt-3 overflow-hidden rounded-[16px] border"
            style={{
              background: isDarkMode ? "#0F1828" : "rgba(255,250,243,0.92)",
              borderColor: isDarkMode
                ? "rgba(102,214,169,0.16)"
                : "rgba(70,150,118,0.16)",
            }}
          >
            <div className="flex items-center justify-between gap-3 px-3 py-2.5">
              <div>
                <div
                  className="text-[8.5px] font-black uppercase tracking-[0.16em]"
                  style={{ color: isDarkMode ? "#78E0BD" : "#28795D" }}
                >
                  ✓ Your catches
                </div>
                <div
                  className="mt-0.5 text-[11px] font-bold"
                  style={{ color: isDarkMode ? "#AEB8C9" : "#756A60" }}
                >
                  {memoryCatches.length} logged in this area
                </div>
              </div>
              {selectedMemoryCatch ? (
                <button
                  type="button"
                  onClick={() => setSelectedMemoryCatchId(null)}
                  className="rounded-full border px-2.5 py-1 text-[9px] font-black"
                  style={{
                    borderColor: isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(91,67,49,0.12)",
                    color: isDarkMode ? "#DDE5F0" : "#665A4F",
                  }}
                >
                  Back
                </button>
              ) : null}
            </div>

            {selectedMemoryCatch ? (
              <div className="border-t px-3 py-3" style={{ borderColor: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(91,67,49,0.08)" }}>
                <div className="flex gap-3">
                  {selectedMemoryCatch.imageUrl ? (
                    <img
                      src={selectedMemoryCatch.imageUrl}
                      alt=""
                      className="h-16 w-16 shrink-0 rounded-[13px] object-cover"
                    />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-black" style={{ color: isDarkMode ? "#F2F6FC" : "#2D3957" }}>
                      {selectedMemoryCatch.species}
                    </div>
                    <div className="mt-1 text-[10.5px] font-semibold" style={{ color: isDarkMode ? "#AEB8C9" : "#756A60" }}>
                      {selectedMemoryCatch.location}
                    </div>
                    <div className="mt-0.5 text-[10px]" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>
                      {formatCatchPopupDate(selectedMemoryCatch.timestamp)}
                    </div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[10.5px]">
                  {selectedMemoryCatch.weight ? (
                    <div><span className="font-black">Weight</span> · {selectedMemoryCatch.weight}</div>
                  ) : null}
                  {String((selectedMemoryCatch.raw as any)?.bait || (selectedMemoryCatch.raw as any)?.lure || "").trim() ? (
                    <div><span className="font-black">Bait / lure</span> · {String((selectedMemoryCatch.raw as any)?.bait || (selectedMemoryCatch.raw as any)?.lure)}</div>
                  ) : null}
                  {String((selectedMemoryCatch.raw as any)?.technique || "").trim() ? (
                    <div className="col-span-2"><span className="font-black">Technique</span> · {String((selectedMemoryCatch.raw as any)?.technique)}</div>
                  ) : null}
                </div>
                {selectedMemoryCatch.note ? (
                  <div className="mt-3 rounded-[12px] px-3 py-2.5 text-[10.5px] font-semibold leading-4" style={{ background: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(245,238,231,0.84)", color: isDarkMode ? "#DDE5F0" : "#665A4F" }}>
                    {selectedMemoryCatch.note}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="border-t" style={{ borderColor: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(91,67,49,0.08)" }}>
                {memoryCatches.slice(0, 3).map((entry) => (
                  <button
                    type="button"
                    key={entry.id}
                    onClick={() => setSelectedMemoryCatchId(entry.id)}
                    className="flex w-full items-center gap-2.5 border-b px-3 py-2.5 text-left"
                    style={{ borderColor: isDarkMode ? "rgba(255,255,255,0.07)" : "rgba(91,67,49,0.07)" }}
                  >
                    {entry.imageUrl ? (
                      <img src={entry.imageUrl} alt="" className="h-9 w-9 shrink-0 rounded-[10px] object-cover" />
                    ) : (
                      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px]" style={{ background: isDarkMode ? "#0C1423" : "#EEF7FA" }}>🎣</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[11px] font-black" style={{ color: isDarkMode ? "#F2F6FC" : "#2D3957" }}>{entry.species}</div>
                      <div className="mt-0.5 truncate text-[9.5px]" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>{formatCatchPopupDate(entry.timestamp)}</div>
                    </div>
                    <span aria-hidden="true" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>›</span>
                  </button>
                ))}

                {onOpenCatchHistory ? (
                  <button
                    type="button"
                    onClick={() => onOpenCatchHistory(commonName)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition active:scale-[0.99]"
                    style={{
                      background: isDarkMode ? "rgba(102,214,169,0.06)" : "rgba(234,255,245,0.72)",
                      color: isDarkMode ? "#CFF5E5" : "#256A54",
                    }}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[10.5px] font-black">
                        View all {memoryCatches.length} {commonName} catches
                      </span>
                      <span className="mt-0.5 block text-[9px] font-semibold opacity-70">
                        Open Catch History filtered to this species
                      </span>
                    </span>
                    <span aria-hidden="true" className="shrink-0 text-[17px] font-black">›</span>
                  </button>
                ) : null}
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div
        className="shrink-0 border-t px-3.5 pb-3.5 pt-3 sm:px-4 sm:pb-4"
        style={{
          borderColor: isDarkMode
            ? "rgba(132,151,190,0.12)"
            : "rgba(91,67,49,0.08)",
        }}
      >
        <div className={onLogCatch ? "grid grid-cols-2 gap-2.5" : "grid grid-cols-1"}>
          <button
            type="button"
            onClick={onOpenFull}
            className="w-full rounded-[14px] px-3 py-3 text-[10.5px] font-black uppercase tracking-[0.12em] transition active:scale-[0.985]"
            style={{
              background: isDarkMode ? "#67D8E8" : "#2F80ED",
              color: isDarkMode ? "#07131A" : "#FFFFFF",
              boxShadow: isDarkMode
                ? "0 5px 14px rgba(24,181,204,0.18)"
                : "0 6px 14px rgba(35,52,91,0.16)",
            }}
          >
            Full Water Read
          </button>

          {onLogCatch ? (
            <button
              type="button"
              onClick={onLogCatch}
              className="w-full rounded-[14px] border px-3 py-3 text-[10.5px] font-black uppercase tracking-[0.12em] transition active:scale-[0.985]"
              style={{
                background: isDarkMode ? "rgba(102,214,169,0.12)" : "rgba(234,255,245,0.96)",
                color: isDarkMode ? "#DFFFF1" : "#256A54",
                borderColor: isDarkMode
                  ? "rgba(102,214,169,0.18)"
                  : "rgba(172,235,204,1)",
              }}
            >
              Log this fish
            </button>
          ) : null}
        </div>
      </div>

      {isActive && isImageExpanded && typeof document !== "undefined"
        ? ReactDOM.createPortal(
            <div
              data-roddo-map-portal="true"
              className="roddo-safe-overlay fixed inset-0 z-[8000] flex items-center justify-center bg-black/90 px-3 backdrop-blur-sm sm:px-6"
              role="dialog"
              aria-modal="true"
              aria-label={`${commonName} reference image`}
              onClick={() => setIsImageExpanded(false)}
            >
              <div
                className="roddo-safe-modal-frame relative flex w-full max-w-5xl flex-col overflow-hidden rounded-[22px] border border-white/15 bg-black/80 shadow-2xl"
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => setIsImageExpanded(false)}
                  className="absolute right-3 top-3 z-10 grid h-10 w-10 place-items-center rounded-full border border-white/20 bg-black/70 text-xl font-bold text-white shadow-lg backdrop-blur-md active:scale-[0.98]"
                  aria-label="Close fish image"
                  title="Close"
                >
                  ×
                </button>

                <div className="flex min-h-[240px] flex-1 items-center justify-center bg-black">
                  <FishIQSpeciesImage
                    species={species}
                    size="hero"
                    fallback={<div className="grid min-h-[240px] w-full place-items-center text-[72px]">🐟</div>}
                    className="max-h-[calc(var(--roddo-visual-vh,100dvh)-120px)] w-full"
                    imageClassName="object-contain"
                    isDarkMode={isDarkMode}
                  />
                </div>

                <div className="border-t border-white/10 bg-black/85 px-4 py-3 text-white sm:px-5">
                  <div className="pr-12 text-[14px] font-black">{commonName}</div>
                  {scientificName ? (
                    <div className="mt-1 text-[11px] italic text-white/65">{scientificName}</div>
                  ) : null}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
    </>
  );
};

export const FishIQThinkingState: React.FC<{
  isDarkMode: boolean;
  error?: string | null;
  onRetry?: (() => void) | null;
}> = ({ isDarkMode, error, onRetry }) => {
  const statuses = [
    "Reading the water…",
    "Checking local signals…",
    "Matching species to conditions…",
    "Building your water read…",
  ];
  const [step, setStep] = useState(0);
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (error) return;
    setStep(0);
    setSlow(false);
    const statusTimer = window.setInterval(() => {
      setStep((current) => Math.min(statuses.length - 1, current + 1));
    }, 900);
    const slowTimer = window.setTimeout(() => setSlow(true), 4200);
    return () => {
      window.clearInterval(statusTimer);
      window.clearTimeout(slowTimer);
    };
  }, [error]);

  if (error) {
    return (
      <div className="mt-2.5 min-h-0 flex-1 overflow-y-auto pr-0.5">
        <div
          className="rounded-[20px] border px-4 py-5 text-center"
          style={{
            background: isDarkMode ? "#0F1828" : "rgba(255,255,255,0.82)",
            borderColor: isDarkMode ? "rgba(242,184,75,0.18)" : "rgba(160,120,40,0.18)",
          }}
        >
          <div
            className="mx-auto grid h-14 w-14 place-items-center rounded-full border"
            style={{
              background: isDarkMode ? "rgba(242,184,75,0.09)" : "rgba(255,248,221,0.92)",
              borderColor: isDarkMode ? "rgba(242,184,75,0.18)" : "rgba(160,120,40,0.18)",
            }}
          >
            <img src={roddoFish} alt="" className="h-8 w-9 object-contain opacity-90" draggable={false} />
          </div>
          <div className="mt-3 text-[14px] font-black" style={{ color: isDarkMode ? "#F6F8FC" : "#34424C" }}>
            Water read paused
          </div>
          <div className="mx-auto mt-1.5 max-w-[300px] text-[11px] font-semibold leading-5" style={{ color: isDarkMode ? "#9BA8BA" : "#756A60" }}>
            {error}
          </div>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="mt-4 h-10 rounded-[13px] border px-5 text-[11px] font-black uppercase tracking-[0.10em] transition active:scale-[0.985]"
              style={{
                background: isDarkMode ? "rgba(102,217,232,0.12)" : "rgba(234,247,255,0.96)",
                borderColor: isDarkMode ? "rgba(102,217,232,0.22)" : "rgba(86,174,220,0.28)",
                color: isDarkMode ? "#DDF6FF" : "#22638A",
              }}
            >
              Try again
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2.5 min-h-0 flex-1 overflow-y-auto pr-0.5">
      <div
        className="overflow-hidden rounded-[20px] border px-4 py-4"
        style={{
          background: isDarkMode ? "#0F1828" : "linear-gradient(180deg, rgba(235,249,252,0.94), rgba(255,251,244,0.96))",
          borderColor: isDarkMode ? "rgba(102,217,232,0.16)" : "rgba(72,147,161,0.18)",
        }}
      >
        <div className="flex items-center gap-3">
          <div className="relative grid h-16 w-16 shrink-0 place-items-center">
            <div
              className="absolute inset-0 animate-ping rounded-full opacity-20"
              style={{ background: isDarkMode ? "#67D8E8" : "#2F80ED", animationDuration: "2.4s" }}
            />
            <div
              className="absolute inset-[7px] animate-pulse rounded-full border"
              style={{
                background: isDarkMode ? "rgba(103,216,232,0.10)" : "rgba(255,255,255,0.78)",
                borderColor: isDarkMode ? "rgba(103,216,232,0.20)" : "rgba(47,128,237,0.16)",
              }}
            />
            <img src={roddoFish} alt="Roddo is reading this water" className="relative z-10 h-8 w-9 object-contain" draggable={false} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[9px] font-black uppercase tracking-[0.18em]" style={{ color: isDarkMode ? "#67D8E8" : "#24779B" }}>
              Roddo is reading the water
            </div>
            <div className="mt-1 text-[15px] font-black leading-5" style={{ color: isDarkMode ? "#F3F8FC" : "#34424C" }}>
              {statuses[step]}
            </div>
            <div className="mt-1 text-[10px] font-semibold leading-4" style={{ color: isDarkMode ? "#8794A8" : "#8A7E72" }}>
              {slow ? "Still working — local and water reads can take a few seconds." : "Using this exact map point for the read."}
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          {["Where", "When", "Throw"].map((label) => (
            <div
              key={label}
              className="rounded-[13px] border px-2.5 py-2.5"
              style={{
                background: isDarkMode ? "rgba(8,14,26,0.54)" : "rgba(255,255,255,0.58)",
                borderColor: isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.08)",
              }}
            >
              <div className="text-[7.5px] font-black uppercase tracking-[0.13em]" style={{ color: isDarkMode ? "#738196" : "#8A7E72" }}>
                {label}
              </div>
              <div className="mt-2 h-2.5 animate-pulse rounded-full" style={{ background: isDarkMode ? "rgba(132,151,190,0.14)" : "rgba(91,67,49,0.10)" }} />
              <div className="mt-1.5 h-2.5 w-2/3 animate-pulse rounded-full" style={{ background: isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.07)" }} />
            </div>
          ))}
        </div>

        <div className="mt-3 grid gap-2">
          {[0, 1, 2].map((row) => (
            <div
              key={row}
              className="flex items-center gap-3 rounded-[15px] border px-3 py-2.5"
              style={{
                background: isDarkMode ? "rgba(8,14,26,0.46)" : "rgba(255,255,255,0.54)",
                borderColor: isDarkMode ? "rgba(132,151,190,0.09)" : "rgba(91,67,49,0.07)",
              }}
            >
              <div className="h-10 w-12 shrink-0 animate-pulse rounded-[11px]" style={{ background: isDarkMode ? "rgba(103,216,232,0.09)" : "rgba(47,128,237,0.08)" }} />
              <div className="min-w-0 flex-1">
                <div className="h-2.5 w-3/5 animate-pulse rounded-full" style={{ background: isDarkMode ? "rgba(132,151,190,0.16)" : "rgba(91,67,49,0.11)" }} />
                <div className="mt-2 h-2 w-2/5 animate-pulse rounded-full" style={{ background: isDarkMode ? "rgba(132,151,190,0.10)" : "rgba(91,67,49,0.07)" }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

